import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { FileText, CheckCircle, Phone, AlertTriangle, ClipboardList, Shield } from "lucide-react";
import Layout from "@/components/Layout";
import SEO from "@/components/SEO";
import SectionFadeIn from "@/components/SectionFadeIn";
import heroContractsImg from "@/assets/hero-contracts.webp";

const faqsEN = [
  {
    q: "Are these templates legal contracts?",
    a: "Our templates are professionally drafted documents designed for NYC construction projects. However, ARCONDES LLC is not a law firm and these templates do not constitute legal advice. We strongly recommend having any contract reviewed by a licensed attorney before execution.",
  },
  {
    q: "Can I customize the templates?",
    a: "Yes. Every template is designed with clear fill-in fields and editable sections so you can adapt them to your specific project. The Key Terms Summary page helps you identify the most important provisions to review and customize.",
  },
  {
    q: "What types of projects are these templates designed for?",
    a: "Our templates are specifically designed for NYC facade repair, FISP compliance work, waterproofing, and related building envelope projects. They address the unique regulatory, insurance, and compliance requirements of construction work in New York City.",
  },
  {
    q: "How are these different from generic contract templates?",
    a: "Generic contract templates cover broad construction scenarios but often miss NYC-specific requirements like FISP compliance provisions, DOB permit coordination, sidewalk shed obligations, and the insurance minimums required for facade work in New York City. Our templates are built specifically for these project types.",
  },
];

const ContractTemplates = () => {
  const { t, i18n } = useTranslation("serviceDetails");
  const prefix = i18n.language === "es" ? "/es" : "";

  const templateIcons = [FileText, ClipboardList, FileText, Shield, FileText];

  return (
    <Layout>
      <SEO
        title="Construction Contract Templates for NYC Building Projects | ARCONDES"
        description="Professional, editable construction contract templates designed for NYC facade repair, FISP compliance, and building envelope projects. Fill-in-the-blank format with Key Terms Summary. Request a quote today."
        schema={[
          {
            "@context": "https://schema.org",
            "@type": "Service",
            "name": "Construction Contract Templates NYC",
            "provider": { "@type": "Organization", "name": "ARCONDES", "telephone": "+1-347-613-1717" },
            "areaServed": { "@type": "City", "name": "New York" },
            "description": "Professional construction contract templates designed for NYC facade repair, FISP work, and building envelope projects.",
            "serviceType": ["Contract Templates", "Construction Documents"],
          },
          {
            "@context": "https://schema.org",
            "@type": "FAQPage",
            "mainEntity": faqsEN.map((f) => ({
              "@type": "Question",
              "name": f.q,
              "acceptedAnswer": { "@type": "Answer", "text": f.a },
            })),
          },
        ]}
      />

      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroContractsImg} alt="Construction contract templates" className="w-full h-full object-cover" loading="eager" />
          <div className="absolute inset-0 bg-gradient-to-r from-foreground/85 via-foreground/60 to-transparent" />
        </div>
        <div className="relative container-max px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <SectionFadeIn>
            <div className="max-w-3xl">
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-primary-foreground leading-tight mb-6">
                {t("contractTemplates.hero.title")} <span className="text-brand-yellow">{t("contractTemplates.hero.titleHighlight")}</span>
              </h1>
              <p className="text-primary-foreground/70 text-lg mb-8 leading-relaxed max-w-2xl">
                {t("contractTemplates.hero.subtitle")}
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Button asChild size="lg" className="bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-sm tracking-wider font-semibold px-8">
                  <Link to={`${prefix}/contact`}>{t("contractTemplates.hero.ctaPrimary")}</Link>
                </Button>
                <Button asChild size="lg" className="bg-primary-foreground text-foreground hover:bg-primary-foreground/90 uppercase text-sm tracking-wider font-semibold px-8">
                  <a href="tel:+13476131717"><Phone className="h-4 w-4 mr-2" />{t("contractTemplates.hero.ctaSecondary")}</a>
                </Button>
              </div>
            </div>
          </SectionFadeIn>
        </div>
      </section>

      {/* Templates Available */}
      <section className="section-padding">
        <div className="container-max">
          <SectionFadeIn>
            <div className="text-center mb-12">
              <div className="h-1 w-12 bg-brand-yellow mx-auto mb-4" />
              <h2 className="text-3xl font-bold text-foreground mb-3">{t("contractTemplates.templatesSection.title")}</h2>
            </div>
          </SectionFadeIn>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {(t("contractTemplates.templatesSection.items", { returnObjects: true }) as { title: string; code: string; desc: string }[]).map((item, i) => {
              const Icon = templateIcons[i % templateIcons.length];
              return (
                <SectionFadeIn key={i}>
                  <div className="bg-secondary p-6 rounded-sm border border-border">
                    <Icon className="h-6 w-6 text-accent mb-3" />
                    <div className="flex items-center gap-2 mb-2">
                      <h3 className="font-semibold text-foreground">{item.title}</h3>
                    </div>
                    <span className="text-xs text-accent font-mono uppercase tracking-wider">{item.code}</span>
                    <p className="text-muted-foreground text-sm leading-relaxed mt-2">{item.desc}</p>
                  </div>
                </SectionFadeIn>
              );
            })}
          </div>
        </div>
      </section>

      {/* Why Our Templates */}
      <section className="section-padding bg-secondary">
        <div className="container-max">
          <div className="grid lg:grid-cols-2 gap-16 items-start">
            <SectionFadeIn>
              <div>
                <h2 className="text-3xl font-bold text-foreground mb-6">{t("contractTemplates.whySection.title")}</h2>
                <div className="space-y-4 text-muted-foreground leading-relaxed">
                  {(t("contractTemplates.whySection.paragraphs", { returnObjects: true }) as string[]).map((p, i) => (
                    <p key={i}>{p}</p>
                  ))}
                </div>
              </div>
            </SectionFadeIn>
            <SectionFadeIn delay={0.1}>
              <div className="bg-background rounded-sm p-8 border border-border">
                <h3 className="text-lg font-bold text-foreground mb-5">{t("contractTemplates.whySection.checklistTitle")}</h3>
                <ul className="space-y-3">
                  {(t("contractTemplates.whySection.checklist", { returnObjects: true }) as string[]).map((item, i) => (
                    <li key={i} className="flex items-start gap-3 text-sm text-muted-foreground">
                      <CheckCircle className="h-4 w-4 text-accent flex-shrink-0 mt-0.5" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </SectionFadeIn>
          </div>
        </div>
      </section>

      {/* Disclaimer */}
      <section className="section-padding">
        <div className="container-max max-w-3xl">
          <SectionFadeIn>
            <div className="bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-sm p-8">
              <div className="flex items-start gap-4">
                <AlertTriangle className="h-6 w-6 text-amber-600 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-lg font-bold text-foreground mb-2">{t("contractTemplates.disclaimer.title")}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{t("contractTemplates.disclaimer.content")}</p>
                </div>
              </div>
            </div>
          </SectionFadeIn>
        </div>
      </section>

      {/* Related Services */}
      <section className="section-padding bg-secondary">
        <div className="container-max">
          <SectionFadeIn>
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center">{t("contractTemplates.relatedServices.title")}</h2>
          </SectionFadeIn>
          <div className="grid sm:grid-cols-3 gap-6">
            {(() => {
              const relatedIcons = [FileText, ClipboardList, Shield];
              const paths = ["/services/construction-consulting", "/resources/construction-contracts-guide", "/services/facade-inspection"];
              return (t("contractTemplates.relatedServices.items", { returnObjects: true }) as { title: string; desc: string }[]).map((s, i) => (
                <SectionFadeIn key={i}>
                  <Link to={`${prefix}${paths[i]}`} className="block bg-background p-6 rounded-sm border border-border hover:border-accent transition-colors">
                    {(() => { const Icon = relatedIcons[i]; return <Icon className="h-6 w-6 text-accent mb-3" />; })()}
                    <h3 className="font-semibold text-foreground mb-2">{s.title}</h3>
                    <p className="text-muted-foreground text-sm">{s.desc}</p>
                  </Link>
                </SectionFadeIn>
              ));
            })()}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="section-padding">
        <div className="container-max max-w-3xl">
          <SectionFadeIn>
            <h2 className="text-3xl font-bold text-foreground mb-8 text-center">{t("contractTemplates.faq.title")}</h2>
          </SectionFadeIn>
          <div className="space-y-6">
            {(t("contractTemplates.faq.items", { returnObjects: true }) as { question: string; answer: string }[]).map((faq, i) => (
              <SectionFadeIn key={i}>
                <div className="border-b border-border pb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-2">{faq.question}</h3>
                  <p className="text-muted-foreground text-sm leading-relaxed">{faq.answer}</p>
                </div>
              </SectionFadeIn>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-foreground section-padding text-center pb-24 sm:pb-28">
        <div className="container-max">
          <SectionFadeIn>
            <h2 className="text-3xl font-bold text-primary-foreground mb-4">{t("contractTemplates.cta.title")}</h2>
            <p className="text-primary-foreground/60 mb-8 max-w-lg mx-auto">{t("contractTemplates.cta.subtitle")}</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button asChild size="lg" className="bg-brand-yellow text-brand-yellow-foreground hover:bg-brand-yellow/90 uppercase text-sm tracking-wider font-semibold px-8">
                <Link to={`${prefix}/contact`}>{t("contractTemplates.cta.ctaPrimary")}</Link>
              </Button>
              <Button asChild size="lg" className="bg-primary-foreground text-foreground hover:bg-primary-foreground/90 uppercase text-sm tracking-wider font-semibold px-8">
                <a href="tel:+13476131717"><Phone className="h-4 w-4 mr-2" />{t("contractTemplates.cta.ctaSecondary")}</a>
              </Button>
            </div>
          </SectionFadeIn>
        </div>
      </section>
    </Layout>
  );
};

export default ContractTemplates;
